import React, { useState } from 'react';
import classNames from 'classnames';
import { EditorView } from '@codemirror/view';
import { EditorState, Extension } from '@codemirror/state';
import MarkdownEditorBase, { type ICommand } from '@uiw/react-markdown-editor';
import { MarkdownHelpModal } from 'Components/organisms/MarkdownHelpModal/MarkdownHelpModal';
import './styles.scss';

type MarkdownEditorProps = {
    id: string;
    value?: string;
    placeholder?: string;
    className?: string;
    error?: boolean;
    disabled?: boolean;
    maxLength?: number;
    showCharacterCount?: boolean;
    onChange: (option: string, fieldName: string, id: string) => void;
    fieldName: string;
};

const TOOLBAR_COMMANDS: Array<'bold' | 'italic' | 'link' | ICommand | 'preview'> = [
    'bold',
    'italic',
    'link',
    {
        name: 'help',
        keyCommand: 'help',
        button: { 'aria-label': 'Markdown help' },
        icon: <span className="markdown-editor-wrapper__help-icon">?</span>,
    },
    'preview',
];

function createMaxLengthExtension(maxLength?: number): Extension[] {
    if (maxLength === undefined) {
        return [EditorView.lineWrapping];
    }

    return [
        EditorView.lineWrapping,
        EditorState.transactionFilter.of((transaction) => {
            if (!transaction.docChanged) {
                return transaction;
            }

            const previousLength = transaction.startState.doc.length;
            const nextLength = transaction.newDoc.length;
            const isReductionFromOverLimit = previousLength > maxLength && nextLength < previousLength;

            if (nextLength <= maxLength || isReductionFromOverLimit) {
                return transaction;
            }

            return [];
        }),
    ];
}

export const MarkdownEditor = ({
    id,
    value,
    placeholder,
    className,
    error,
    disabled,
    maxLength,
    showCharacterCount = false,
    onChange,
    fieldName,
}: MarkdownEditorProps) => {
    const [showHelp, setShowHelp] = useState(false);
    const currentLength = (value ?? '').length;

    const helpCommand: ICommand = {
        name: 'help',
        keyCommand: 'help',
        button: { 'aria-label': 'Markdown help' },
        icon: <span className="markdown-editor-wrapper__help-icon">?</span>,
        execute: () => {
            setShowHelp(true);
        },
    };

    const toolbarCommandsWithHelp = TOOLBAR_COMMANDS.map((cmd) =>
        typeof cmd === 'string' ? cmd : helpCommand,
    ) as ('bold' | 'italic' | 'link' | 'preview' | ICommand)[];

    const handleChange = (nextValue: string = '') => {
        if (disabled) {
            return;
        }
        onChange(nextValue, fieldName, id);
    };

    return (
        <div
            className={classNames(
                'markdown-editor-wrapper',
                'side-panel__markdown-editor',
                { 'markdown-editor-wrapper--error': error },
                className,
            )}
        >
            <>
                <MarkdownEditorBase
                    value={value ?? ''}
                    onChange={(next) => handleChange(next)}
                    enableScroll={false}
                    height="auto"
                    previewWidth="100%"
                    showToolbar
                    enablePreview={false}
                    toolbars={toolbarCommandsWithHelp}
                    toolbarsMode={[]}
                    visible
                    className="markdown-editor-wrapper__editor"
                    placeholder={placeholder}
                    basicSetup={{ lineNumbers: false, foldGutter: false }}
                    extensions={createMaxLengthExtension(maxLength)}
                />
                {showCharacterCount && (
                    <div className="markdown-editor-wrapper__character-count">
                        {maxLength !== undefined ? `${currentLength}/${maxLength}` : `${currentLength}`}
                    </div>
                )}
                <MarkdownHelpModal show={showHelp} onClose={() => setShowHelp(false)} />
            </>
        </div>
    );
};

export default MarkdownEditor;

